import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Boxes, CalendarClock, CheckCircle2, ClipboardCheck, Package, Users } from "lucide-react";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const MODULES = [
  { name: "Stock", desc: "Inventario y pedidos", stat: "98% abastecido", color: "#22C55E", Icon: Boxes },
  { name: "Calidad", desc: "Controles y evidencia", stat: "12 controles hoy", color: "#3B82F6", Icon: ClipboardCheck },
  { name: "Personas", desc: "Turnos y asistencia", stat: "Turnos al día", color: "#F59E0B", Icon: Users },
];

const ModulePanel = () => (
  <div className="w-full max-w-md rounded-3xl border border-[#1F3D35]/8 bg-white p-6 shadow-[0_24px_64px_rgba(15,77,63,0.10)]">
    <div className="mb-5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#C6F04A]" />
        <span className="text-sm font-semibold tracking-wide text-[#1F3D35]">Suite TUPU</span>
      </div>
      <span className="rounded-full bg-[#DCECCF] px-3 py-1 text-xs font-semibold text-[#0F4D3F]">En línea</span>
    </div>
    <div className="space-y-3">
      {MODULES.map(({ name, desc, stat, color, Icon }, i) => (
        <motion.div
          key={name}
          data-testid={`hero-module-${name.toLowerCase()}`}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 + i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 rounded-2xl border border-[#1F3D35]/6 bg-[#F8F8F3] p-4"
        >
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: `${color}1A`, color }}
          >
            <Icon size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#1F3D35]">TUPU {name}</p>
            <p className="truncate text-xs text-[#1F3D35]/55">{desc}</p>
          </div>
          <div className="text-right">
            <span className="mb-1.5 flex items-center justify-end gap-1.5 text-[11px] font-medium text-[#1F3D35]/60">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
              {stat}
            </span>
            <div className="h-1 w-20 overflow-hidden rounded-full bg-[#1F3D35]/8">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: color }}
                initial={{ width: "0%" }}
                animate={{ width: `${88 - i * 9}%` }}
                transition={{ delay: 1 + i * 0.15, duration: 0.8, ease: "easeOut" }}
              />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const Badge = ({ className, delay, Icon, color, children, testId }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
    transition={{
      opacity: { delay, duration: 0.5 },
      scale: { delay, duration: 0.5 },
      y: { delay: delay + 0.5, duration: 4.5, repeat: Infinity, ease: "easeInOut" },
    }}
    className={`absolute z-10 flex items-center gap-2 rounded-full border border-[#1F3D35]/8 bg-white px-4 py-2.5 text-xs font-semibold text-[#1F3D35] shadow-[0_12px_32px_rgba(15,77,63,0.12)] ${className}`}
  >
    <Icon size={15} style={{ color }} />
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#DCECCF]/50 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-48 top-64 h-[380px] w-[380px] rounded-full bg-[#F3EBD7]/70 blur-3xl"
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-20 pt-16 md:px-12 lg:grid-cols-12 lg:pb-28 lg:pt-24">
        <motion.div style={{ y: textY }} className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-[#0F4D3F]/15 bg-white px-4 py-2"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 rounded-full bg-[#C6F04A] ring-4 ring-[#C6F04A]/25" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#0F4D3F]">
              Mide. Registra. Cumple.
            </span>
          </motion.div>

          <h1 className="text-5xl font-semibold leading-[1.04] tracking-tighter text-[#1F3D35] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <Line delay={0.1}>Tu operación,</Line>
            <Line delay={0.22}>más simple y</Line>
            <Line delay={0.34}>
              <span className="relative inline-block text-[#0F4D3F]">
                bajo control.
                <svg
                  aria-hidden
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 300 12"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M4 8C60 3 150 2 296 7"
                    stroke="#C6F04A"
                    strokeWidth="6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1.05, duration: 0.7, ease: "easeOut" }}
                  />
                </svg>
              </span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-7 max-w-lg text-base font-light leading-relaxed text-[#1F3D35]/75 md:text-lg"
            data-testid="hero-subtext"
          >
            TUPU reúne herramientas para gestionar inventario, calidad y personas desde una misma plataforma.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#productos"
              data-testid="hero-cta-explorar"
              className="rounded-full bg-[#0F4D3F] px-7 py-3.5 text-sm font-semibold text-[#F3EBD7] shadow-[0_12px_32px_rgba(15,77,63,0.25)] transition-[background-color,transform] duration-200 hover:bg-[#0c3f33] active:scale-[0.98]"
            >
              Explorar soluciones
            </a>
            <a
              href="#ecosistema"
              data-testid="hero-cta-conocer"
              className="rounded-full border border-[#1F3D35]/20 px-7 py-3.5 text-sm font-semibold text-[#1F3D35] transition-colors hover:bg-[#F3EBD7]"
            >
              Conocer TUPU
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: visualY }} className="relative lg:col-span-6" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto flex max-w-md justify-center lg:max-w-none"
          >
            <ModulePanel />
            <Badge testId="hero-badge-pedido" className="-left-4 top-8 hidden sm:flex lg:-left-10" delay={1.2} Icon={Package} color="#22C55E">
              Pedido recibido
            </Badge>
            <Badge testId="hero-badge-control" className="-right-3 top-1/2 hidden sm:flex lg:-right-8" delay={1.4} Icon={CheckCircle2} color="#3B82F6">
              Control aprobado
            </Badge>
            <Badge testId="hero-badge-turno" className="-left-2 bottom-6 hidden sm:flex lg:-left-6" delay={1.6} Icon={CalendarClock} color="#F59E0B">
              Turno asignado
            </Badge>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
